/* DC Sponsors v2. Hanging holographic cloth banners (dc-holocloth.js) with a roll-down
   entrance, over a particle field that morphs between the show mark and each sponsor's
   logo (dc-logomorph.js). No Spline runtime anywhere in this block.

   Structure: init(root) is idempotent and teardown(root) undoes it, so the theme editor's
   section reload (shopify:section:load) and the far-away release on phones both work.
   Motion resting states are set here, never in CSS, so the block is complete without JS. */
(() => {
  const MOBILE = '(max-width: 767px)';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const touchDevice = () => matchMedia('(hover: none), (pointer: coarse)').matches;
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const RELEASE_MS = 8000, RELEASE_VH = 1.5;

  /* ---- Entrance -----------------------------------------------------------------------
     Rods drop in, each cloth unrolls from its rod (scaleY from the top edge) left to right,
     the foil logo fades up once the cloth has cleared, wordmark lines rise, vendors follow.
     GSAP when the event runtime has loaded it, a CSS transition fallback otherwise. */
  const parts = (root) => ({
    banners: [...root.querySelectorAll('.dc-sp2__banner')],
    rods: [...root.querySelectorAll('.dc-sp2__rod')],
    cloths: [...root.querySelectorAll('.dc-sp2__cloth')],
    logos: [...root.querySelectorAll('.dc-sp2__print')],
    lines: [...root.querySelectorAll('.dc-sp2__wordmark > span')],
    kickers: [...root.querySelectorAll('.dc-sp2__kicker')],
    vendors: [...root.querySelectorAll('.dc-sp2__vendors li')]
  });

  const arm = (root) => {
    const p = parts(root);
    const g = window.gsap;
    if (g) {
      g.set(p.rods, { y: -18, autoAlpha: 0 });
      g.set(p.cloths, { scaleY: 0, transformOrigin: '50% 0%' });
      g.set(p.logos, { autoAlpha: 0 });
      g.set(p.lines, { y: 26, autoAlpha: 0 });
      g.set(p.kickers, { y: 10, autoAlpha: 0 });
      g.set(p.vendors, { y: 14, autoAlpha: 0 });
      return;
    }
    const set = (els, css) => els.forEach((el) => Object.assign(el.style, css));
    set(p.rods, { transform: 'translateY(-18px)', opacity: '0' });
    set(p.cloths, { transform: 'scaleY(0)' });
    set(p.logos, { opacity: '0' });
    set(p.lines, { transform: 'translateY(26px)', opacity: '0' });
    set(p.kickers, { transform: 'translateY(10px)', opacity: '0' });
    set(p.vendors, { transform: 'translateY(14px)', opacity: '0' });
  };

  const roll = (root) => {
    if (root.__dcRolled) return;
    root.__dcRolled = true;
    const p = parts(root);
    const g = window.gsap;
    if (g) {
      const tl = g.timeline({ defaults: { ease: 'power3.out' } });
      tl.to(p.kickers[0], { y: 0, autoAlpha: 1, duration: .6 }, 0)
        .to(p.lines, { y: 0, autoAlpha: 1, duration: .8, stagger: .09 }, .05)
        .to(p.rods, { y: 0, autoAlpha: 1, duration: .5, stagger: .07 }, .1)
        .to(p.cloths, { scaleY: 1, duration: 1.15, ease: 'back.out(1.12)', stagger: .09 }, .28)
        .to(p.logos, { autoAlpha: 1, duration: .7, stagger: .07 }, .95)
        .to(p.kickers.slice(1), { y: 0, autoAlpha: 1, duration: .5, stagger: .18 }, 1.2)   // every white-tier kicker (two since 2026-09-22)
        .to(p.vendors, { y: 0, autoAlpha: 1, duration: .6, stagger: .06 }, 1.3)
        .add(() => root.classList.add('is-rolled'));
      root.__dcTl = tl;
      return;
    }
    const go = (els, css, base, step, ms) => els.forEach((el, i) => {
      el.style.transition = 'transform ' + ms + 'ms cubic-bezier(.16,1,.3,1) ' + (base + i * step) + 'ms, opacity ' + ms + 'ms ease ' + (base + i * step) + 'ms';
      requestAnimationFrame(() => Object.assign(el.style, css));
    });
    go(p.kickers, { transform: 'none', opacity: '1' }, 0, 1100, 600);
    go(p.lines, { transform: 'none', opacity: '1' }, 50, 90, 800);
    go(p.rods, { transform: 'none', opacity: '1' }, 100, 70, 500);
    go(p.cloths, { transform: 'scaleY(1)' }, 280, 90, 1150);
    go(p.logos, { opacity: '1' }, 950, 70, 700);
    go(p.vendors, { transform: 'none', opacity: '1' }, 1300, 60, 600);
    setTimeout(() => root.classList.add('is-rolled'), 2400);
  };

  /* ---- The sheets ---------------------------------------------------------------------
     Mounted by dc-holocloth.js when the block nears the viewport (six WebGL2 contexts are
     not free), torn down with the section. */
  const mountSheets = (root) => {
    if (root.__dcSheets || !window.__dcHolo) return;
    root.__dcSheets = true;
    try { window.__dcHolo.init(root); } catch (e) { root.__dcSheets = false; }
  };
  const dropSheets = (root) => {
    if (!root.__dcSheets || !window.__dcHolo) return;
    try { window.__dcHolo.destroy(root); } catch (e) {}
    root.__dcSheets = false;
  };

  /* ---- Visibility, release, init ------------------------------------------------------ */
  const teardown = (root) => {
    if (!root.__dcInit) return;
    root.__dcInit = false;
    root.__dcVisible = false;
    try { root.__dcIO && root.__dcIO.disconnect(); } catch (e) {}
    try { root.__dcLazy && root.__dcLazy.disconnect(); } catch (e) {}
    try { root.__dcFar && root.__dcFar.disconnect(); } catch (e) {}
    dropSheets(root);
    try { root.__dcTl && root.__dcTl.kill(); } catch (e) {}
    dropBg(root);
    root.__dcIO = root.__dcLazy = root.__dcFar = root.__dcTl = null;
  };

  const releaseWhenFar = (root) => {
    if (!touchDevice() || !('IntersectionObserver' in window)) return null;
    let timer = 0, near = true;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        near = en.isIntersecting;
        clearTimeout(timer); timer = 0;
        if (near) return;
        timer = setTimeout(() => { timer = 0; if (near) return; dropBg(root); }, RELEASE_MS);
      });
    }, { rootMargin: Math.round(RELEASE_VH * 100) + '% 0px' });
    io.observe(root);
    return { disconnect() { clearTimeout(timer); timer = 0; try { io.disconnect(); } catch (e) {} } };
  };

  const init = (root) => {
    if (root.__dcInit) return;
    root.__dcInit = true;
    root.__dcVisible = false;
    root.__dcRolled = false;
    const motion = !reduced && 'IntersectionObserver' in window;
    if (motion) arm(root);

    /* Roll when a quarter of the block is on screen; keep the particle field and the
       idle sway running only while it is. */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          root.__dcVisible = en.isIntersecting;
          if (en.isIntersecting && motion && en.intersectionRatio >= 0.22) roll(root);
        });
      }, { threshold: [0, 0.22, 0.5] });
      io.observe(root);
      root.__dcIO = io;
    } else if (motion) roll(root);

    /* Touch devices: once the block is a screen and a half away for a while, give every
       WebGL context back (six cloth sheets, the wave field, the dotted field). iOS bills
       them all against the page and starts dropping decoded images elsewhere, which is how
       the About block's poster went black on a phone. They come back when the block nears. */
    if (touchDevice() && 'IntersectionObserver' in window) {
      let timer = 0;
      const far = new IntersectionObserver((entries) => {
        const near = entries.some((en) => en.isIntersecting);
        clearTimeout(timer); timer = 0;
        if (near) {
          mountSheets(root);
          try { window.__dcWave && window.__dcWave.init(root); } catch (e) {}
          try { window.__dcMorph && window.__dcMorph.init(root); } catch (e) {}
        } else {
          timer = setTimeout(() => {
            dropSheets(root);
            try { window.__dcWave && window.__dcWave.destroy(root); } catch (e) {}
            try { window.__dcMorph && window.__dcMorph.destroy(root); } catch (e) {}
          }, RELEASE_MS);
        }
      }, { rootMargin: Math.round(RELEASE_VH * 100) + '% 0px' });
      far.observe(root);
      root.__dcFar = far;
    }
    /* The sheets mount one screen early, whatever the connection. */
    if ('IntersectionObserver' in window) {
      const lz = new IntersectionObserver((entries, obs) => { if (entries.some((en) => en.isIntersecting)) { obs.disconnect(); mountSheets(root); } }, { rootMargin: '400px 0px' });
      lz.observe(root);
      root.__dcLazy = lz;
    } else mountSheets(root);
  };

  const initAll = (scope) => (scope || document).querySelectorAll('[data-dc-sponsors2]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
    document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('[data-dc-sponsors2]').forEach(teardown));
  }
})();
